import type { CarOnsaDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/motor';
import { useState, type ChangeEvent } from 'react';
import { GridItem, Box, Link, SelectField } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useUpdateCoveragePackageInformation } from '../../../../recoil/hooks/useUpdateCoveragePackageInformation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { deductibleOptionsSelector } from '../../../../recoil/selectors/deductibleOptionsSelector';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { yearlyMileageOptionsSelector } from '../../../../recoil/selectors/yearlyMileageOptionsSelector';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCoverable } from '../../../../utils/coverablesUtil';
import CalculatorModal from './CalculatorModal';
import { refreshCoveragePackagePricesAtom } from '../../../../recoil/atoms/refreshCoveragePackagePricesAtom';
import { refreshCoveragePackagesAtom } from '../../../../recoil/atoms/refreshCoveragePackagesAtom';

const CoveragePackageCarInputs = ({
    isLoading,
    setIsLoading
}: {
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { updateCoveragePackageInformation } = useUpdateCoveragePackageInformation<CarOnsaDTO>();
    const refreshPrices = useSetRecoilState(refreshCoveragePackagePricesAtom);
    const [showModal, setShowModal] = useState(false);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { flavourName = '', fixedId = '' } = currentProduct ?? {};
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable = getOnsaCoverable(coverables, flavourName, fixedId) as CarOnsaDTO;
    const deductibleOptions = useRecoilValue(deductibleOptionsSelector('MVPLiability'));
    const mileageOptions = useRecoilValue(yearlyMileageOptionsSelector);
    const [yearlyMileage, setYearlyMileage] = useState(coverable.yearlyMileage?.code);
    const [deductible, setDeductible] = useState(coverable.deductible);
    const refreshPackages = useSetRecoilState(refreshCoveragePackagesAtom);

    const changeDeductible = (event: ChangeEvent<HTMLSelectElement>): void => {
        const deductible = event.target.value;
        setIsLoading(true);
        setDeductible(deductible);
        const option = deductibleOptions.find((elt) => elt.optionID === deductible);
        if (!option) {
            setIsLoading(false);
            return;
        }
        updateCoverableInformation({ deductible: option.optionID });
        void updateCoveragePackageInformation({ deductible: option.optionID }).then(() => {
            refreshPackages({});
            setIsLoading(false);
        });
    };

    const changeMileage = (mileageCode: string): void => {
        setIsLoading(true);
        const option = mileageOptions.find((elt) => elt.code === mileageCode);
        if (!option) {
            setIsLoading(false);
            return;
        }
        updateCoverableInformation({ yearlyMileage: option });
        void updateCoveragePackageInformation({ yearlyMileage: option }).then(() => {
            refreshPrices({});
            setIsLoading(false);
        });
    };

    return (
        <GridItem span={7}>
            <Box>
                <div className="u-flex-box u-flex-box--space-between">
                    <h4 className="u-spacing-stack-none">
                        {getHippoText('DSS-COVERAGE-PACKAGE-PAGE-YEARLY-MILEAGE-HEADER')}
                    </h4>
                    <Link
                        theme="logo"
                        className={
                            isLoading
                                ? 'u-text-right u-spacing-stack-bottom-l u-color-supplement-grey-medium'
                                : 'u-text-right u-spacing-stack-bottom-l'
                        }
                        onClick={() => {
                            if (isLoading) return;
                            setShowModal(true);
                        }}
                    >
                        {getHippoText('DSS-ACTIVATION-FLOW-YEARLY-MILEAGE-CALCULATOR')}
                    </Link>
                </div>
                <div>
                    <SelectField
                        placeholder={yearlyMileage}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                            setYearlyMileage(event.target.value);
                            changeMileage(event.target.value);
                        }}
                        label={getHippoText('DSS-COVERAGE-PACKAGE-PAGE-YEARLY-MILEAGE-FIELD-TEXT')}
                        options={mileageOptions.map((option) => {
                            return { label: option.displayName, value: option.code };
                        })}
                        value={yearlyMileage}
                        disabled={isLoading}
                    ></SelectField>
                </div>
                <div className="u-spacing-inset-m--stretch-max">
                    <div className="u-flex-box u-flex-box--space-between">
                        <h4 className="u-spacing-stack-none">
                            {getHippoText('DSS-COVERAGE-PACKAGE-PAGE-DEDUCTIBLE-HEADER')}
                        </h4>
                    </div>
                    <div>
                        <SelectField
                            onChange={changeDeductible}
                            label={getHippoText('DSS-COVERAGE-PACKAGE-PAGE-DEDUCTIBLE-FIELD-TEXT')}
                            options={deductibleOptions.map((option) => {
                                return { label: option.displayName, value: option.optionID };
                            })}
                            value={deductible}
                            disabled={isLoading}
                        ></SelectField>
                    </div>
                </div>
                {showModal && (
                    <CalculatorModal
                        setYearlyMileage={setYearlyMileage}
                        changeMileage={changeMileage}
                        showModal={showModal}
                        closeModal={() => {
                            setShowModal(false);
                        }}
                    />
                )}
            </Box>
        </GridItem>
    );
};

export default CoveragePackageCarInputs;
